// Stripe service for handling payments
// This service uses Stripe's client-side checkout

// You'll need to install: npm install @stripe/stripe-js @stripe/react-stripe-js
import { loadStripe } from '@stripe/stripe-js';
import API_BASE_URL from '../config';
import { fetchCsrfToken } from './api';

// Replace with your actual Stripe publishable key
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_stripe_publishable_key_here';

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

// Product IDs from your Stripe dashboard
const STRIPE_PRODUCTS = {
  qa_chatbot: {
    monthly: 'price_1S7Sr5IEBR1uElKN0CzkSBjk' // Q&A Chatbot Access - $6/month
  },
  beginner: {
    monthly: 'price_1S7SruIEBR1uElKNVqnKMHUn', // Replace with your actual price ID
    annual: 'price_1S7SrlIEBR1uElKNMm3fa3E4'    // Replace with your actual price ID
  },
  standard: {
    monthly: 'price_1S7SrYIEBR1uElKNCpyGGFZJ', // Replace with your actual price ID
    annual: 'price_1S7SrSIEBR1uElKNRaFB8yr3'    // Replace with your actual price ID
  },
  pro: {
    monthly: 'price_1S7SrMIEBR1uElKNjpUDpCra',     // Replace with your actual price ID
    annual: 'price_1S7SrGIEBR1uElKNUEcYnQwb'        // Replace with your actual price ID
  }
};

// Create checkout session (server-side approach with promotion codes support)
export const createCheckoutSession = async (planId, billingCycle, customerEmail = null) => {
  try {
    const csrfToken = await fetchCsrfToken();
    
    const response = await fetch(`${API_BASE_URL}/api/create_checkout_session/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken,
      },
      credentials: 'include',
      body: JSON.stringify({
        planId,
        billingCycle,
        customerEmail
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create checkout session');
    }

    const data = await response.json();
    return data.sessionId;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

// Redirect to Stripe Checkout
export const redirectToCheckout = async (sessionId) => {
  try {
    const stripe = await stripePromise;
    const { error } = await stripe.redirectToCheckout({
      sessionId: sessionId
    });

    if (error) {
      throw new Error(error.message);
    }

    return { success: true };
  } catch (error) {
    console.error('Error redirecting to checkout:', error);
    throw error;
  }
};

// Handle successful payment (frontend-only approach)
export const handlePaymentSuccess = async (sessionId) => {
  try {
    // For now, simulate successful payment verification
    console.log('Verifying payment for session:', sessionId);
    
    // In a real implementation, you would verify the payment with Stripe
    // This could be done client-side or with a webhook
    
    return { success: true, sessionId };
  } catch (error) {
    console.error('Error verifying payment:', error);
    throw error;
  }
};

// Get customer's current subscription (frontend-only approach)
export const getCurrentSubscription = async () => {
  try {
    // For now, simulate getting subscription info
    console.log('Getting current subscription...');
    
    // In a real implementation, you would get this from Stripe or your backend
    return { 
      plan: 'free', 
      status: 'active',
      currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days from now
    };
  } catch (error) {
    console.error('Error getting subscription:', error);
    throw error;
  }
};

// Cancel subscription (frontend-only approach)
export const cancelSubscription = async () => {
  try {
    // For now, simulate subscription cancellation
    console.log('Canceling subscription...');
    
    // In a real implementation, you would cancel the subscription with Stripe
    return { success: true, message: 'Subscription canceled successfully' };
  } catch (error) {
    console.error('Error canceling subscription:', error);
    throw error;
  }
};

export default {
  createCheckoutSession,
  redirectToCheckout,
  handlePaymentSuccess,
  getCurrentSubscription,
  cancelSubscription
};
